import { collect } from 'collect.js';
import { ManagerRole } from '../permissions/PermissionResolver';

/**
 * Pure multi-tenant selection decisions extracted from InitialDataProvider and
 * BusinessProfileProvider. Keeping these out of the providers lets us unit-test
 * the tenant-isolation rules without mocking the whole async provider tree.
 *
 * See tests/services/tenant/tenantSelection.test.js.
 */

const STORE_LEVEL_ROLES = [ManagerRole.StoreAdmin, ManagerRole.StoreOperator, ManagerRole.StoreEmployee];

/**
 * Whether a franchise should be auto-preselected: only for store-level users who
 * are scoped to fewer than two franchises (a single-franchise context).
 */
export function shouldPreselectFranchise(profile, franchiseAcl = []) {
  const isStoreLevel = (profile?.roles ?? []).some((role) => STORE_LEVEL_ROLES.includes(role));

  return isStoreLevel && (franchiseAcl?.length ?? 0) < 2;
}

/**
 * Pick which franchise to preselect: the currently-active one if it's in the list,
 * otherwise the first available. Franchises (brands) without companies are skipped,
 * because with no company to select the user would land on an empty dashboard with
 * no Menu section. Only when every franchise is empty do we fall back to them, so the
 * empty-brand message is still shown.
 */
export function pickFranchiseToPreselect(franchises, currentFranchise) {
  const withCompanies = collect(franchises).filter((franchise) => (franchise?.companies?.length ?? 0) > 0);
  const candidates = withCompanies.isEmpty() ? collect(franchises) : withCompanies;

  return (
    candidates.when(currentFranchise, (collection) => collection.where('id', currentFranchise.id)).first() ??
    candidates.first()
  );
}

/**
 * Whether the active company should be (re)loaded for a freshly selected franchise:
 * when the franchise has fewer than two companies, or the active company is not one
 * of the franchise's companies.
 */
export function shouldReloadFranchiseCompany(franchise, companyId) {
  return franchise.companies.length < 2 || collect(franchise.companies).where('id', companyId).isEmpty();
}

/**
 * The update scope for a business-profile change: 'all' applies across every
 * company automatically when the merchant has fewer than two; null means the user
 * must choose (a modal is shown).
 */
export function resolveUpdateScope(companies) {
  return (companies?.length ?? 0) < 2 ? 'all' : null;
}

/**
 * Build the list of sibling companies for the business-profile editor: parent +
 * children companies, excluding the active company, sorted by id, with the active
 * company prepended.
 */
export function buildBusinessProfileCompanies(profile, company) {
  return collect(profile?.companies)
    .merge(collect(profile?.children).pluck('companies').flatten(1).toArray())
    .reject((item) => item.id === company.id)
    .sortBy('id')
    .when(Boolean(company.id), (collection) =>
      collection.prepend({
        id: company.id,
        name: company.name,
        address: company.address,
        // The switcher tags the partner's test restaurant, the active one included.
        is_test_restaurant: company.is_test_restaurant,
      }),
    )
    .toArray();
}
